import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";
import {
  Lock,
  ArrowRight,
  Loader2,
  Shield,
  KeyRound,
  Copy,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

// 3 possible steps:
//   "password"  → enter email + password
//   "verify_2fa"  → enter 6-digit code from authenticator (2FA already enabled)
//   "setup_2fa"   → scan QR code, then enter code to enroll (first time)
export default function AdminLogin() {
  const { user, login, setup2FA, verifySetup, verify2FA } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [challengeToken, setChallengeToken] = useState("");
  const [setupToken, setSetupToken] = useState("");
  const [setupData, setSetupData] = useState(null); // {secret, qr_code, otpauth_uri}
  const [secretCopied, setSecretCopied] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const goBack = () => {
    setStep("password");
    setCode("");
    setChallengeToken("");
    setSetupToken("");
    setSetupData(null);
  };

  const handlePassword = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      toast.error("Email and password required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await login(email.trim(), password);
      if (res?.ok) {
        toast.success("Welcome back.");
        navigate("/admin", { replace: true });
        return;
      }
      if (res?.requires_2fa) {
        setChallengeToken(res.challenge_token);
        setStep("verify_2fa");
        return;
      }
      if (res?.requires_setup) {
        setSetupToken(res.setup_token);
        // immediately fetch the QR / secret
        try {
          const data = await setup2FA(res.setup_token);
          setSetupData(data);
          setStep("setup_2fa");
        } catch (err) {
          toast.error(
            formatApiErrorDetail(err?.response?.data?.detail) ||
              "Could not start 2FA setup"
          );
        }
        return;
      }
      toast.error("Unexpected response from server");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Login failed"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code.trim())) {
      toast.error("Enter the 6-digit code from your authenticator");
      return;
    }
    setSubmitting(true);
    try {
      await verify2FA(challengeToken, code.trim());
      toast.success("Welcome back.");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Invalid code"
      );
      setCode("");
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifySetup = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code.trim())) {
      toast.error("Enter the 6-digit code from your authenticator");
      return;
    }
    setSubmitting(true);
    try {
      await verifySetup(setupToken, code.trim());
      toast.success("2FA enabled. You're in.");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Invalid code, try again"
      );
      setCode("");
    } finally {
      setSubmitting(false);
    }
  };

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(setupData.secret);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy. Long-press / select manually.");
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen bg-[#FF5B17] text-[#0A0A0A] flex items-center justify-center px-4 py-10"
    >
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="font-display text-5xl tracking-tight">R⋆TAMA</div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] mt-2">
            Admin · Restricted
          </div>
        </div>

        <div
          className="bg-[#FAF5E8] border-2 border-[#0A0A0A] rounded-2xl p-7 lg:p-8"
          style={{ boxShadow: "8px 8px 0 #0A0A0A" }}
        >
          {step === "password" && (
            <form
              onSubmit={handlePassword}
              data-testid="admin-login-form"
              className="space-y-6"
            >
              <Header
                icon={<Lock className="w-4 h-4 text-[#FAF5E8]" />}
                iconBg="bg-[#FF2D9C]"
                title="Sign in"
                subtitle="Step 1 · Email & password"
              />

              <div>
                <label
                  htmlFor="admin-email"
                  className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#0A0A0A]/70"
                >
                  Email
                </label>
                <input
                  id="admin-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="rtamaltd@gmail.com"
                  className="input-zine"
                  data-testid="admin-login-email"
                  autoComplete="email"
                />
              </div>
              <div>
                <label
                  htmlFor="admin-password"
                  className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#0A0A0A]/70"
                >
                  Password
                </label>
                <input
                  id="admin-password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input-zine"
                  data-testid="admin-login-password"
                  autoComplete="current-password"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="admin-login-submit"
                className="btn-chunky w-full justify-center disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Signing in
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          )}

          {step === "verify_2fa" && (
            <form
              onSubmit={handleVerify2FA}
              data-testid="admin-2fa-verify-form"
              className="space-y-6"
            >
              <Header
                icon={<Shield className="w-4 h-4 text-[#0A0A0A]" />}
                iconBg="bg-[#FFD500]"
                title="Two-factor"
                subtitle="Step 2 · 6-digit code"
              />
              <p className="font-mono text-xs text-[#0A0A0A]/70 leading-relaxed">
                Open your authenticator app (Google Authenticator, Authy, 1Password…)
                and enter the current 6-digit code for <strong>RTAMA LTD</strong>.
              </p>
              <CodeInput value={code} onChange={setCode} testid="admin-2fa-code" />
              <div className="flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={goBack}
                  className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0A0A0A]/60 hover:text-[#0A0A0A]"
                >
                  ← Use a different account
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="admin-2fa-submit"
                  className="btn-chunky disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Verifying
                    </>
                  ) : (
                    <>
                      Verify
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {step === "setup_2fa" && setupData && (
            <form
              onSubmit={handleVerifySetup}
              data-testid="admin-2fa-setup-form"
              className="space-y-5"
            >
              <Header
                icon={<KeyRound className="w-4 h-4 text-[#FAF5E8]" />}
                iconBg="bg-[#5B2BFF]"
                title="Enable 2FA"
                subtitle="First-time setup · ~30s"
              />

              <ol className="space-y-3 font-mono text-[11px] leading-relaxed text-[#0A0A0A]/80">
                <li>
                  <span className="font-display text-[#FF2D9C] mr-1.5">①</span>
                  Install <strong>Google Authenticator</strong>, Authy or 1Password
                  on your phone.
                </li>
                <li>
                  <span className="font-display text-[#FF2D9C] mr-1.5">②</span>
                  Scan the QR below (or copy the secret manually).
                </li>
                <li>
                  <span className="font-display text-[#FF2D9C] mr-1.5">③</span>
                  Type the 6-digit code your app shows.
                </li>
              </ol>

              {/* QR + secret */}
              <div className="flex items-center gap-4 bg-[#F2EBDC] border-2 border-[#0A0A0A] rounded-xl p-3">
                <img
                  src={setupData.qr_code}
                  alt="QR code for 2FA setup"
                  data-testid="admin-2fa-qr"
                  className="w-32 h-32 bg-white p-1"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#0A0A0A]/60 mb-1">
                    Manual secret
                  </div>
                  <div
                    data-testid="admin-2fa-secret"
                    className="font-mono text-[11px] break-all bg-[#FAF5E8] border border-[#0A0A0A]/20 rounded p-2"
                  >
                    {setupData.secret}
                  </div>
                  <button
                    type="button"
                    onClick={copySecret}
                    data-testid="admin-2fa-secret-copy"
                    className="mt-2 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] hover:text-[#FF2D9C]"
                  >
                    {secretCopied ? (
                      <>
                        <CheckCircle2 className="w-3.5 h-3.5" /> Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5" /> Copy
                      </>
                    )}
                  </button>
                </div>
              </div>

              <CodeInput value={code} onChange={setCode} testid="admin-2fa-setup-code" />

              <div className="flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={goBack}
                  className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0A0A0A]/60 hover:text-[#0A0A0A]"
                >
                  ← Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="admin-2fa-setup-submit"
                  className="btn-chunky disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Enabling
                    </>
                  ) : (
                    <>
                      Enable 2FA
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#0A0A0A]/50 mt-7 text-center">
            RTAMA LTD · HE433506 · Paphos, Cyprus
          </p>
        </div>
      </div>
    </div>
  );
}

function Header({ icon, iconBg, title, subtitle }) {
  return (
    <div className="flex items-center gap-3 mb-5">
      <div
        className={`w-10 h-10 rounded-full ${iconBg} border-2 border-[#0A0A0A] flex items-center justify-center`}
      >
        {icon}
      </div>
      <div>
        <div className="font-display text-2xl">{title}</div>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#0A0A0A]/60">
          {subtitle}
        </div>
      </div>
    </div>
  );
}

function CodeInput({ value, onChange, testid }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#0A0A0A]/70">
        6-digit code
      </label>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="one-time-code"
        maxLength={6}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, ""))}
        placeholder="000 000"
        data-testid={testid}
        className="input-zine text-center tracking-[0.6em] text-2xl font-mono"
        style={{ letterSpacing: "0.4em" }}
        autoFocus
      />
    </div>
  );
}
